import {
  Dmgw6DeviceFunkcheckStatus,
  useDmgw6Store,
} from '@/store/dmgw6/dmgw6Store';
import { useDeviceStore } from '@/store/entities/deviceStore';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { deviceTypes, submeteringDeviceTypes } from '@/enums/device';
import {
  type DMGScannedDevice,
  GatewayStatus,
  ScanSource,
  ScanState,
} from '@/models/installationPoint/InstallationPoint';

function getInstalledDeviceSerials(): Set<string> {
  const deviceStore = useDeviceStore();
  const installationPointStore = useInstallationPointStore();
  const installed = new Set<string>();
  for (const ip of installationPointStore.installationPoints.values()) {
    if (!ip.activeDeviceId) continue;
    const device = deviceStore.devices.get(ip.activeDeviceId);
    if (device?.serialNumber) installed.add(device.serialNumber);
  }
  return installed;
}

export function useDmgw6FunkcheckSync() {
  function writeGatewayDataToInstallationPoints(): void {
    const dmgw6Store = useDmgw6Store();
    const deviceStore = useDeviceStore();
    const installationPointStore = useInstallationPointStore();

    const gatewayDevices = [...deviceStore.devices.values()].filter(
      (d) => d.deviceType === deviceTypes.DirectMeterGateway
    );

    const installedSerials = getInstalledDeviceSerials();

    for (const gateway of gatewayDevices) {
      if (!gateway.serialNumber) continue;
      const stored = dmgw6Store.gatewayFunkcheckData.get(gateway.serialNumber);
      if (!stored) continue;

      const ip = [...installationPointStore.installationPoints.values()].find(
        (ip) => ip.activeDeviceId === gateway.id
      );
      if (!ip) continue;

      const funkcheckData =
        ip.gatewayStatus ?? (ip.gatewayStatus = new GatewayStatus());

      if (stored.connectionStatus) {
        funkcheckData.connectionStatus = stored.connectionStatus;
      }
      if (stored.status) {
        funkcheckData.status = stored.status;
      }
      if (stored.devices.size > 0) {
        const devices =
          funkcheckData.devices ?? (funkcheckData.devices = new Map());
        stored.devices.forEach((entries, deviceSerial) => {
          if (!installedSerials.has(deviceSerial)) return;
          const existing = devices.get(deviceSerial) ?? [];
          for (const entry of entries) {
            const idx = existing.findIndex((e) => e.source === entry.source);
            if (idx >= 0) existing[idx] = entry;
            else existing.push(entry);
          }
          devices.set(deviceSerial, existing);
        });
      }
    }
  }

  function saveGatewayDataToInstallationPoints(): void {
    const deviceStore = useDeviceStore();
    const installationPointStore = useInstallationPointStore();
    const entitiesStore = useEntitiesStore();
    const dmgw6Store = useDmgw6Store();

    const gatewayDevices = [...deviceStore.devices.values()].filter(
      (d) => d.deviceType === deviceTypes.DirectMeterGateway
    );

    const installedSerials = getInstalledDeviceSerials();

    for (const gateway of gatewayDevices) {
      if (!gateway.serialNumber) continue;
      const stored = dmgw6Store.gatewayFunkcheckData.get(gateway.serialNumber);
      if (!stored) continue;

      const ip = [...installationPointStore.installationPoints.values()].find(
        (ip) => ip.activeDeviceId === gateway.id
      );
      if (!ip) continue;

      const gatewayStatus =
        ip.gatewayStatus ?? (ip.gatewayStatus = new GatewayStatus());

      if (stored.connectionStatus) {
        gatewayStatus.connectionStatus = stored.connectionStatus;
      }
      if (stored.status) {
        gatewayStatus.status = stored.status;
      }
      if (stored.devices.size > 0) {
        const devices =
          gatewayStatus.devices ?? (gatewayStatus.devices = new Map());
        stored.devices.forEach((entries, deviceSerial) => {
          if (!installedSerials.has(deviceSerial)) return;
          const existing = devices.get(deviceSerial) ?? [];
          for (const entry of entries) {
            const idx = existing.findIndex((e) => e.source === entry.source);
            if (idx >= 0) existing[idx] = entry;
            else existing.push(entry);
          }
          devices.set(deviceSerial, existing);
        });
      }

      entitiesStore.saveEntity(ip);
    }
  }

  function hydrateFromInstallationPoints(): void {
    const dmgw6Store = useDmgw6Store();
    const deviceStore = useDeviceStore();
    const installationPointStore = useInstallationPointStore();

    dmgw6Store.clearGatewayFunkcheckData();
    dmgw6Store.clearFunkcheckStatuses();

    const gatewayDevices = [...deviceStore.devices.values()].filter(
      (d) => d.deviceType === deviceTypes.DirectMeterGateway
    );
    const foundByGateway = new Set<string>();
    const foundByQtool = new Set<string>();
    let anyGatewayFinished = false;

    for (const gateway of gatewayDevices) {
      if (!gateway.serialNumber) continue;

      const ip = [...installationPointStore.installationPoints.values()].find(
        (ipEntry) => ipEntry.activeDeviceId === gateway.id
      );
      const persisted = ip?.gatewayStatus;
      if (!persisted) continue;

      if (persisted.connectionStatus) {
        dmgw6Store.setGatewayLteCheckData(
          gateway.serialNumber,
          persisted.connectionStatus
        );
      }
      if (persisted.status) {
        dmgw6Store.setGatewayScanStatus(gateway.serialNumber, persisted.status);
        if (persisted.status === ScanState.FINISHED) anyGatewayFinished = true;
      }
      const rawDevices = persisted.devices;
      const devicesMap: Map<string, DMGScannedDevice[]> | undefined =
        rawDevices instanceof Map
          ? rawDevices
          : rawDevices && typeof rawDevices === 'object'
            ? new Map(
                Object.entries(rawDevices as Record<string, DMGScannedDevice[]>)
              )
            : undefined;

      if (devicesMap && devicesMap.size > 0) {
        dmgw6Store.bulkUpsertGatewayScannedDevices(
          gateway.serialNumber,
          devicesMap
        );

        for (const [deviceSerial, entries] of devicesMap) {
          for (const entry of entries) {
            if (entry.source === ScanSource.QTOOL_GATEWAY) {
              foundByGateway.add(deviceSerial);
            } else if (entry.source === ScanSource.QTOOL) {
              foundByQtool.add(deviceSerial);
            }
          }
        }
      }
    }

    if (foundByGateway.size === 0 && foundByQtool.size === 0) return;

    const updates = new Map<string, Dmgw6DeviceFunkcheckStatus>();
    for (const device of deviceStore.devices.values()) {
      if (!submeteringDeviceTypes.includes(device.deviceType)) continue;
      if (!device.serialNumber) continue;
      if (!device.isAutomatedMeterReading) continue;

      if (foundByGateway.has(device.serialNumber)) {
        updates.set(device.id, Dmgw6DeviceFunkcheckStatus.success);
      } else if (foundByQtool.has(device.serialNumber)) {
        updates.set(device.id, Dmgw6DeviceFunkcheckStatus.partial);
      } else if (anyGatewayFinished) {
        updates.set(device.id, Dmgw6DeviceFunkcheckStatus.error);
      }
    }

    if (updates.size > 0) {
      dmgw6Store.bulkSetDeviceFunkcheckStatuses(updates);
    }
  }

  return {
    writeGatewayDataToInstallationPoints,
    saveGatewayDataToInstallationPoints,
    hydrateFromInstallationPoints,
  };
}
